import { put, select, takeLatest } from "redux-saga/effects";
import {
  setAkaConsent,
  setAkaConsentFailed,
  setAkaConsentSucceeded,
} from "./akaConsentSlice";
import {
  marketingConsentUpdateFailed,
  setMarketingConsented,
  updateMarketingConsent,
  updateMarketingConsentSucceeded,
} from "./marketingConsentSlice";

import { Cart } from "Store/cart/cartInterfaces";
import { cartSelector } from "Store/cart/cartSagas";
import { ReduxState } from "Store/rootReducer";
import { UserIdentificationData } from "Store/userIdentification";
import { userIdentificationSelector } from "Store/userIdentification/userIdentificationSelectors";
import {
  getNin as getNinFromAuth,
  isAuthenticated,
} from "Utils/KeycloakWrapper";
import { getErrorMessage } from "Utils/errorUtils";
import { Log, isLogEntryError } from "Utils/sentryLogUtils";
import {
  postAkaConsentForCustomer,
  postMarketingConsentForCustomer,
} from "./consentClient";
import { ConsentState } from "./consentInterfaces";

const noCartOrEmptyCart = (cart: Cart | null): boolean =>
  !(cart?.products ? cart?.products.length > 0 : false);

const getNin = (user: UserIdentificationData): string | undefined => {
  if (isAuthenticated()) {
    return getNinFromAuth();
  }
  return user?.nin;
};

const marketingConsentSelector = (state: ReduxState): ConsentState =>
  state.marketingConsent;

export function* setDefaultConsentForMarketing({
  payload,
}: ReturnType<typeof setMarketingConsented>): any {
  const consent = yield select(marketingConsentSelector);
  const cart = yield select(cartSelector);
  const user = yield select(userIdentificationSelector);
  if (noCartOrEmptyCart(cart)) {
    try {
      const nin = getNin(user);
      if (nin) {
        yield postMarketingConsentForCustomer(true, payload, nin);
        yield put(updateMarketingConsentSucceeded(true));
      }
    } catch (e) {
      if (isLogEntryError(e)) {
        Log.error(e);
      }
      yield put(
        marketingConsentUpdateFailed(
          getErrorMessage(e, "Error updating marketing consents"),
        ),
      );
    }
  } else {
    yield put(updateMarketingConsentSucceeded(consent.consented));
  }
}

export function* updateConsentForMarketing({
  payload,
}: ReturnType<typeof updateMarketingConsent>): any {
  const consent = yield select(marketingConsentSelector);
  const user = yield select(userIdentificationSelector);
  const nin = getNin(user);
  const updatedConsent = !consent.consented;
  if (nin) {
    try {
      yield postMarketingConsentForCustomer(updatedConsent, payload, nin);
      yield put(updateMarketingConsentSucceeded(updatedConsent));
    } catch (e) {
      if (isLogEntryError(e)) {
        Log.error(e);
      }
      yield put(
        marketingConsentUpdateFailed(
          getErrorMessage(e, "Failed to save Marketing consents"),
        ),
      );
    }
  } else {
    yield put(
      marketingConsentUpdateFailed("no customer nin for marketing consent"),
    );
  }
}

export function* setConsentForAka({
  payload,
}: ReturnType<typeof setAkaConsent>): any {
  try {
    yield postAkaConsentForCustomer(payload);
    yield put(setAkaConsentSucceeded());
  } catch (e) {
    if (isLogEntryError(e)) {
      Log.error(e);
    }
    yield put(
      setAkaConsentFailed(getErrorMessage(e, "Failed to set Aka consents")),
    );
  }
}

export function* consentSaga(): Generator {
  yield takeLatest(setMarketingConsented, setDefaultConsentForMarketing);
  yield takeLatest(updateMarketingConsent, updateConsentForMarketing);
  yield takeLatest(setAkaConsent, setConsentForAka);
}
